import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The loading indicator for content that is already on screen and is being
 * replaced — a refresh, a re-run of the same search, a filter change that
 * keeps the previous answer visible while the next one arrives.
 *
 * It is an overlay rather than a tint on the content because a tint alone is
 * not an indicator: it says "something is different about this" and leaves the
 * reader to guess what. A moving spinner is the only part of the treatment that
 * says work is happening, so it is what carries the message; the dimming
 * underneath is just contrast for it.
 *
 * The parent must be positioned — every surface using this sets `relative`.
 * It must not sit inside an `overflow` box either: the spinner sticks to the
 * visible part of a tall surface, and an overflow ancestor would pin it there
 * instead of to the page.
 *
 * Use a skeleton instead when there is nothing on screen yet: an overlay over
 * an empty box indicates nothing.
 */
export function BusyOverlay({
  show,
  size = 'default',
  className,
}: {
  show?: boolean;
  /** `sm` for stat tiles and other small surfaces. */
  size?: 'sm' | 'default';
  className?: string;
}) {
  const { t } = useTranslation();
  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // `rounded-[inherit]` so the overlay follows whatever corner radius the
      // surface has instead of squaring off a rounded card.
      className={cn(
        'bg-background/50 absolute inset-0 z-20 rounded-[inherit]',
        className
      )}
    >
      {/*
        Centred in the surface, but never more than half a screen down and
        sticky while it scrolls: centred on a full page of rows, the spinner
        sat below the fold and the refresh looked like nothing was happening.
      */}
      <div className="sticky top-0 flex h-full max-h-[50svh] items-center justify-center">
        <Loader2
          className={cn(
            'text-primary animate-spin',
            size === 'sm' ? 'size-4' : 'size-6'
          )}
        />
      </div>
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}
